import { Button, makeStyles } from "@material-ui/core";
import { InfoOutlined } from "@material-ui/icons";
import ContentHeader from "common/components/ContentHeader/ContentHeader";
import ContentPanel from "common/components/ContentPanel/ContentPanel";
import HtmlTooltip from "common/components/HtmlTooltip/HtmlTooltip";
import { SetLoading } from "common/components/Loader/Loader";
import UserCard from "common/components/UserCard";
import { connect } from "common/connect";
import StoreKeys from "common/storeKeys";
import * as React from "react";
import NameplateService from "services/NameplateService";
import UserService from "services/UserService";
import { Colors } from "styles/Colors";

const useStyles = makeStyles({
	categoryHeading: {
		color: Colors.pickstop,
		fontSize: 13,
		letterSpacing: 1,
		textTransform: "uppercase",
		marginTop: 0,
		marginBottom: 0,
		display: "flex",
		alignItems: "center",
	},
	groupHeading: {
		color: Colors.primaryDark,
		fontSize: 13,
		fontWeight: "bold",
		marginTop: 8,
		marginBottom: 4,
	},
	row: {
		display: "flex",
		alignItems: "center",
		justifyContent: "space-between",
		marginBottom: 6,
		"&:last-child": {
			marginBottom: 0,
		},
	},
	infoIcon: {
		fontSize: 18,
		marginLeft: 6,
		cursor: "help",
	},
	tooltipName: {
		display: "block",
		fontWeight: "bold",
	},
	tooltipDescription: {
		display: "block",
		fontStyle: "italic",
		marginTop: 2,
	},
	progress: {
		display: "flex",
		alignItems: "center",
	},
	progressTrack: {
		backgroundColor: Colors.body,
		borderRadius: 4,
		height: 8,
		width: 70,
	},
	progressFill: {
		backgroundColor: Colors.pickstop,
		borderRadius: 4,
		height: "100%",
	},
	progressText: {
		color: Colors.primaryDark,
		fontSize: 12,
		marginLeft: 8,
		minWidth: 32,
		textAlign: "right",
	},
	equipButton: {
		color: Colors.pickstop,
		borderColor: Colors.pickstop,
		fontSize: 12,
		lineHeight: 1,
		padding: "7px 8px 5px",
	},
	equippedLabel: {
		color: Colors.pickstopGreen,
		fontSize: 12,
		fontWeight: "bold",
	},
});

const groups = [
	{ metric: "EventWinner", label: "Event Wins" },
	{ metric: "SecondPlace", label: "Second Place" },
	{ metric: "Top10", label: "Top 10" },
	{ metric: "Top10Pct", label: "Top 10%" },
];

const rulesTooltip =
	"Better finishes stack: winning an event also counts toward Top 10 and Top 10%. " +
	"Only events with 50 or more participants count. " +
	"Progress counts events from all games combined.";

const propKeys = {
	userId: StoreKeys.USER.ID,
	username: StoreKeys.USER.USERNAME,
};

function AchievementProgressC({ userId, username }) {
	const styles = useStyles();
	const [rows, setRows] = React.useState([]);
	const [plates, setPlates] = React.useState([]);
	const [equippedNameplateId, setEquippedNameplateId] = React.useState(null);

	React.useEffect(() => {
		// Progress can resolve before the app-wide nameplate init, so load both.
		Promise.all([
			NameplateService.loadCachedNameplates(),
			NameplateService.getAchievementProgress(),
		]).then(([loadedPlates, progress]) => {
			setPlates(loadedPlates);
			setRows(Array.isArray(progress) ? progress : []);
		});

		if (userId) {
			UserService.getUserCardInfo(userId).then((info) => {
				setEquippedNameplateId(info?.nameplateId ?? null);
			});
		} else {
			setEquippedNameplateId(null);
		}
	}, [userId]);

	const equip = (nameplateId) => {
		SetLoading(true, "Changing Nameplate");
		NameplateService.setNameplate(nameplateId).then(() => {
			setEquippedNameplateId(nameplateId);
			SetLoading(false);
		});
	};

	const renderRow = (row) => {
		const nameplate = plates.find((p) => p.id === row.namePlateStyleId);
		const equipped = equippedNameplateId === row.namePlateStyleId;

		return (
			<div className={styles.row} key={row.namePlateStyleId}>
				<HtmlTooltip
					title={
						<>
							<span className={styles.tooltipName}>{nameplate?.name}</span>
							{nameplate?.description && (
								<span className={styles.tooltipDescription}>
									{nameplate.description}
								</span>
							)}
						</>
					}
				>
					<span>
						<UserCard
							preview
							dontLoadData
							nameplateId={row.namePlateStyleId}
							userName={username ?? nameplate?.name ?? "Nameplate"}
						/>
					</span>
				</HtmlTooltip>
				{row.owned && equipped && (
					<span className={styles.equippedLabel}>Equipped</span>
				)}
				{row.owned && !equipped && (
					<Button
						size="small"
						variant="outlined"
						className={styles.equipButton}
						onClick={() => equip(row.namePlateStyleId)}
					>
						Equip
					</Button>
				)}
				{!row.owned && (
					<div className={styles.progress}>
						<div className={styles.progressTrack}>
							<div
								className={styles.progressFill}
								style={{
									width:
										Math.min(100, (row.value / row.threshold) * 100) + "%",
								}}
							/>
						</div>
						<span className={styles.progressText}>
							{row.value}/{row.threshold}
						</span>
					</div>
				)}
			</div>
		);
	};

	return (
		<ContentPanel>
			<ContentHeader text="Achievements" />
			<div className={styles.categoryHeading}>
				Placements
				<HtmlTooltip title={rulesTooltip}>
					<InfoOutlined className={styles.infoIcon} />
				</HtmlTooltip>
			</div>
			{groups.map((group) => {
				const groupRows = rows
					.filter((row) => row.metric === group.metric)
					.sort((a, b) => a.threshold - b.threshold);

				if (groupRows.length === 0) {
					return null;
				}

				return (
					<div key={group.metric}>
						<div className={styles.groupHeading}>{group.label}</div>
						{groupRows.map(renderRow)}
					</div>
				);
			})}
		</ContentPanel>
	);
}

export default function AchievementProgress(props) {
	return connect(<AchievementProgressC />, propKeys, props);
}
